import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Debug } from "components/global/DebugModal";
import LanguagePicker from "components/languagePicker";
import { useDoorAndLockState } from "hooks/useDoorAndLockState";
import { useOrder } from "hooks/useOrder";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import type { Ref } from "react";
import { selectIsReportingIssue } from "slices/appStateSlice";
import { selectFinishedLocalRefillId } from "slices/transactionSlice";
import { DoorState, LockState } from "types";
import { REFILL_SUMMARY_TIMEOUT_SECONDS } from "utils/designTokens";
import { AutolockTimer, BackToHomeButton, DrawerOrderSummaryRow, TutorialModal } from "./components";

type DrawerActionBarProps = {
	currentOrderStep: number;
	ref?: Ref<HTMLDivElement>;
};

/**
 * App action bar, that handles displays basic information about user and order process
 */
export const DrawerActionBar = ({ currentOrderStep, ref }: DrawerActionBarProps) => {
	const { lockTimeout, doors, lock } = useDoorAndLockState();
	const { finishOrder } = useOrder();
	const isReportingIssue = useAppSelector(selectIsReportingIssue);
	const finishedLocalRefillId = useAppSelector(selectFinishedLocalRefillId);
	const isDebug = import.meta.env.MODE !== "production";
	const handleFinishOrder = () => {
		finishOrder();
	};

	// Suspend the autolock countdown while ReportIssueModal is open so its onTimeout
	// can't dispatch finishOrder and unmount the modal mid-interaction.
	const doorsClosedAndUnlocked =
		lockTimeout && doors === DoorState.Closed && lock === LockState.Unlocked && !isReportingIssue;

	return (
		<ActionBar
			ref={ref}
			className="actionBar"
			sx={{
				width: currentOrderStep === 1 ? "330px" : "100vw",
			}}
			data-testid="action-bar"
		>
			<LanguagePicker />
			<TutorialModal />
			{isDebug && <Debug />}
			{doorsClosedAndUnlocked && <AutolockTimer lockTimeout={lockTimeout} onTimeout={handleFinishOrder} />}
			<DrawerOrderSummaryRow orderStep={currentOrderStep} />
			{/* Keyed by the finished refill: the 60 s start when the summary appears, even if step 5 began earlier. */}
			{currentOrderStep === 5 && !isReportingIssue && (
				<BackToHomeButton
					key={finishedLocalRefillId ?? "order"}
					finishOrder={() => handleFinishOrder()}
					seconds={finishedLocalRefillId ? REFILL_SUMMARY_TIMEOUT_SECONDS : undefined}
					labelKey={finishedLocalRefillId ? "order.refillSummary.confirm" : undefined}
				/>
			)}
		</ActionBar>
	);
};

const ActionBar = styled(Box)(({ theme }) => ({
	position: "fixed",
	bottom: 0,
	left: 0,
	right: 0,
	padding: "40px ",
	display: "flex",
	zIndex: 1201,
	gap: theme.spacing(2),
	alignItems: "center",
}));

DrawerActionBar.displayName = "DrawerActionBar";
